"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CombinedAtomViewImpl = exports.JsonAtom = exports.AbstractAtom = exports.AbstractReadOnlyAtom = void 0;
/* eslint-disable no-nested-ternary,no-plusplus */
const lens_1 = require("@rixio/lens");
const rxjs_1 = require("rxjs");
class AbstractReadOnlyAtom extends rxjs_1.BehaviorSubject {
    constructor() {
        super(...arguments);
        this.viewedAtomsCache = new lens_1.SimpleCache(lens => new AtomViewImpl(this, lens.get));
    }
    view(...args) {
        if (args[0] === undefined) {
            return this;
        }
        else if (typeof args[0] === "function") {
            return new AtomViewImpl(this, args[0]);
        }
        else if (typeof args[0] === "string") {
            const lens = lens_1.Lens.compose(...args.map(lens_1.Lens.key()));
            return this.viewedAtomsCache.getOrCreate(lens);
        }
        else {
            return new AtomViewImpl(this, x => args[0].get(x));
        }
    }
}
exports.AbstractReadOnlyAtom = AbstractReadOnlyAtom;
class AbstractAtom extends AbstractReadOnlyAtom {
    constructor() {
        super(...arguments);
        this.lensedAtomsCache = new lens_1.SimpleCache(lens => new LensedAtom(this, lens, lens_1.structEq));
    }
    set(x) {
        this.modify(() => x);
    }
    lens(arg1, ...args) {
        const lens = typeof arg1 === "string" ? lens_1.Lens.compose(lens_1.Lens.key(arg1), ...args.map(k => lens_1.Lens.key(k))) : arg1;
        return this.lensedAtomsCache.getOrCreate(lens);
    }
}
exports.AbstractAtom = AbstractAtom;
class JsonAtom extends AbstractAtom {
    constructor(initialValue) {
        super(initialValue);
    }
    get() {
        return this.getValue();
    }
    modify(updateFn) {
        const prevValue = this.getValue();
        const next = updateFn(prevValue);
        if (!lens_1.structEq(prevValue, next))
            this.next(next);
    }
    set(x) {
        const prevValue = this.getValue();
        if (!lens_1.structEq(prevValue, x))
            this.next(x);
    }
}
exports.JsonAtom = JsonAtom;
class LensedAtom extends AbstractAtom {
    constructor(_source, _lens, _eq = lens_1.structEq) {
        // @NOTE this is a major hack to optimize for not calling
        // _lens.get the extra time here. This makes the underlying
        // BehaviorSubject to have an `undefined` for it's current value.
        //
        // But it works because before somebody subscribes to this
        // atom, it will subscribe to the _source (which we expect to be a
        // descendant of BehaviorSubject as well), which will emit a
        // value right away, triggering our _onSourceValue.
        super(undefined);
        this._source = _source;
        this._lens = _lens;
        this._eq = _eq;
        this._subscription = null;
        this._refCount = 0;
    }
    get() {
        // Optimization: in case we're already subscribed to the
        // source atom, the BehaviorSubject.getValue will return
        // an up-to-date computed lens value.
        //
        // This way we don't need to recalculate the lens value
        // every time.
        return this._subscription ? this.getValue() : this._lens.get(this._source.get());
    }
    modify(updateFn) {
        this._source.modify(x => this._lens.modify(updateFn, x));
    }
    set(newValue) {
        this._source.modify(x => this._lens.set(newValue, x));
    }
    _onSourceValue(x) {
        const prevValue = this.getValue();
        const next = this._lens.get(x);
        if (!this._eq(prevValue, next))
            this.next(next);
    }
    // Rx method overrides
    _subscribe(subscriber) {
        // tslint:disable-line function-name
        if (!this._subscription) {
            this._subscription = this._source.subscribe(x => this._onSourceValue(x));
        }
        this._refCount++;
        const sub = new rxjs_1.Subscription(() => {
            if (--this._refCount <= 0 && this._subscription) {
                this._subscription.unsubscribe();
                this._subscription = null;
            }
        });
        sub.add(super._subscribe(subscriber));
        return sub;
    }
    unsubscribe() {
        if (this._subscription) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
        this._refCount = 0;
        super.unsubscribe();
    }
}
class AtomViewImpl extends AbstractReadOnlyAtom {
    constructor(_source, _getter, _eq = lens_1.structEq) {
        // @NOTE this is a major hack to optimize for not calling
        // _getter the extra time here. This makes the underlying
        // BehaviorSubject to have an `undefined` for it's current value.
        //
        // But it works because before somebody subscribes to this
        // atom, it will subscribe to the _source (which we expect to be a
        // descendant of BehaviorSubject as well), which will emit a
        // value right away, triggering our _onSourceValue.
        super(undefined);
        this._source = _source;
        this._getter = _getter;
        this._eq = _eq;
        this._subscription = null;
        this._refCount = 0;
    }
    get() {
        // Optimization: in case we're already subscribed to the
        // source atom, the BehaviorSubject.getValue will return
        // an up-to-date computed lens value.
        //
        // This way we don't need to recalculate the view value
        // every time.
        return this._subscription ? this.getValue() : this._getter(this._source.get());
    }
    _onSourceValue(x) {
        const prevValue = this.getValue();
        const next = this._getter(x);
        if (!this._eq(prevValue, next))
            this.next(next);
    }
    // Rx method overrides
    _subscribe(subscriber) {
        // tslint:disable-line function-name
        if (!this._subscription) {
            this._subscription = this._source.subscribe(x => this._onSourceValue(x));
        }
        this._refCount++;
        const sub = new rxjs_1.Subscription(() => {
            if (--this._refCount <= 0 && this._subscription) {
                this._subscription.unsubscribe();
                this._subscription = null;
            }
        });
        sub.add(super._subscribe(subscriber));
        return sub;
    }
    unsubscribe() {
        if (this._subscription) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
        this._refCount = 0;
        super.unsubscribe();
    }
}
class CombinedAtomViewImpl extends AbstractReadOnlyAtom {
    constructor(_sources, _combineFn, _eq = lens_1.structEq) {
        // @NOTE this is a major hack to optimize for not calling
        // _combineFn and .get for each source the extra time here.
        // This makes the underlying BehaviorSubject to have an
        // `undefined` for it's current value.
        //
        // But it works because before somebody subscribes to this
        // atom, it will subscribe to the _source (which we expect to be a
        // descendant of BehaviorSubject as well), which will emit a
        // value right away, triggering our _onSourceValue.
        super(undefined);
        this._sources = _sources;
        this._combineFn = _combineFn;
        this._eq = _eq;
        this._subscription = null;
        this._refCount = 0;
    }
    get() {
        // Optimization: in case we're already subscribed to
        // source atoms, the BehaviorSubject.getValue will return
        // an up-to-date computed view value.
        //
        // This way we don't need to recalculate the view value
        // every time.
        return this._subscription ? this.getValue() : this._combineFn(this._sources.map(x => x.get()));
    }
    _onSourceValues(xs) {
        const prevValue = this.getValue();
        const next = this._combineFn(xs);
        if (!this._eq(prevValue, next))
            this.next(next);
    }
    // Rx method overrides
    _subscribe(subscriber) {
        // tslint:disable-line function-name
        if (!this._subscription) {
            this._subscription = rxjs_1.combineLatest(this._sources).subscribe(xs => this._onSourceValues(xs));
        }
        this._refCount++;
        const sub = new rxjs_1.Subscription(() => {
            if (--this._refCount <= 0 && this._subscription) {
                this._subscription.unsubscribe();
                this._subscription = null;
            }
        });
        sub.add(super._subscribe(subscriber));
        return sub;
    }
    unsubscribe() {
        if (this._subscription) {
            this._subscription.unsubscribe();
            this._subscription = null;
        }
        this._refCount = 0;
        super.unsubscribe();
    }
}
exports.CombinedAtomViewImpl = CombinedAtomViewImpl;
